/**
 * Transfer and redemption requests started from someone else's account view. The shareholder
 * wizards (index.js) file a request for the account being viewed, never for the viewer:
 *
 * - An observer (the viewed shareholder is in the observer's own Users.clients[client].alias) goes
 *   through the wizard the shareholder would. user/submit-transfer-request.py and
 *   user/submit-redemption.py (NewServerless) verify the observer relationship and use the
 *   shareholder path.
 * - An admin previewing the account submits on the shareholder's behalf through the same admin
 *   path as /admin/requests' Create Request (admin-create-request.js): no bank-digits confirmation,
 *   and the request is marked initiated_on_behalf by the backend.
 * - A supervisor or executive preview cannot submit: those roles have no authority to file a
 *   request for another shareholder.
 *
 * In every case the backend records the viewer as initiated_by.
 */

/**
 * How a request started from the current account view is submitted.
 *
 * targetUserid: the shareholder to send as target_userid, or null to submit as the viewer's own
 * request. byAdmin: the admin on-behalf path. disabled: submission is not allowed from this view.
 *
 * @param {{ viewedUserid?: string | null; authedUser?: string | null; isAdmin?: boolean; isPreviewSession?: boolean }} params
 * @returns {{ targetUserid: string | null; byAdmin: boolean; disabled: boolean }}
 */
export function onBehalfRequestContext({ viewedUserid, authedUser, isAdmin, isPreviewSession }) {
	const own = { targetUserid: null, byAdmin: false, disabled: false };
	if (!viewedUserid || !authedUser || viewedUserid === 'consolidated.view') return own;
	if (viewedUserid.trim().toLowerCase() === authedUser.trim().toLowerCase()) return own;
	if (isAdmin) return { targetUserid: viewedUserid, byAdmin: true, disabled: false };
	if (isPreviewSession) return { targetUserid: null, byAdmin: false, disabled: true };
	return { targetUserid: viewedUserid, byAdmin: false, disabled: false };
}

/**
 * inputData with target_userid set; inputData unchanged when targetUserid is null.
 *
 * @param {Record<string, any>} inputData
 * @param {string | null} targetUserid
 */
export function withTargetUserid(inputData, targetUserid) {
	return targetUserid ? { ...inputData, target_userid: targetUserid } : inputData;
}

/**
 * fieldsConfig without the bank-digits confirmation on the admin path (the shareholder's own
 * confirmation, which the backend ignores there -- same as admin-create-request.js's loadTypeConfig).
 *
 * @param {Record<string, boolean>} fieldsConfig
 * @param {boolean} byAdmin
 */
export function fieldsConfigFor(fieldsConfig, byAdmin) {
	return byAdmin ? { ...fieldsConfig, bank_confirmation: false } : fieldsConfig;
}

/**
 * The observer who submitted a request on the shareholder's behalf, or null. An observer submits
 * on the shareholder path, so the execution carries no initiated_on_behalf; the backend's
 * initiated_by (context.initiated_by, or initiatedBy on a normalized row) names the observer
 * instead of the shareholder. Admin on-behalf requests (initiated_on_behalf) are not observer
 * requests.
 *
 * @param {Record<string, any> | null | undefined} execution
 * @returns {string | null} the observer's userid, lowercased
 */
export function observerInitiator(execution) {
	const raw = execution?.raw ?? execution ?? {};
	const inputData = raw.input_data ?? {};
	if (inputData.initiated_on_behalf) return null;
	const by = String(execution?.initiatedBy || raw.context?.initiated_by || '')
		.trim()
		.toLowerCase();
	const shareholder = String(inputData.shareholder_email ?? '')
		.trim()
		.toLowerCase();
	if (!by || !shareholder || by === shareholder) return null;
	return by;
}

// #5559: who may observe whom. An observer acts as the account (signs, votes, fills forms), so
// observing stays between ordinary accounts; roles come only from Change User Role.
// Only alias lists are stored: if Oscar's alias list includes Alex, Oscar observes Alex.

// Who may import wiring/direct deposit instructions for any account (mirrors BANK_INSTRUCTION_IMPORTERS in
// NewServerless user/common/helpers/user_writes.py, which enforces it).
export const BANK_INSTRUCTION_IMPORTERS = ['gmcconnell@nthround.com', 'cmcconnell@nthround.com', 'ahall@nthround.com'];

/**
 * Whether the signed-in user may save wire or direct deposit instructions for the account being viewed:
 * the account itself, one of its observers (on the viewer's alias list), one of the client's own Admins
 * (not a View Only Admin, and not other Nth Round staff), or a bank-instruction importer. Mirrors
 * payout_write_violation in NewServerless user/common/helpers/user_writes.py, which enforces it.
 * @param {string} authedUser
 * @param {string} accountUserid
 * @param {{ aliases?: string[] | null, isAdmin?: boolean, isVOA?: boolean }} viewer aliases null = unknown (the server decides)
 */
export function canEditPayoutInstructions(authedUser, accountUserid, { aliases = null, isAdmin = false, isVOA = false } = {}) {
	const me = cleanId(authedUser);
	const acct = cleanId(accountUserid);
	if (!me || !acct) return false;
	if (me === acct || BANK_INSTRUCTION_IMPORTERS.includes(me)) return true;
	if (isAdmin) return !isVOA && !me.endsWith('@nthround.com');
	if (aliases == null) return true;
	return aliases.some((a) => cleanId(a) === acct);
}

export const ROLE_LABELS = {
	admin: 'Admin',
	voa: 'View Only Admin (VOA)',
	exec: 'Executive',
	supv: 'Supervisor',
	cont: 'Contributor',
	inactive: 'Inactive',
};

const cleanId = (id) =>
	String(id || '')
		.trim()
		.toLowerCase();

/** True for a +subaccount such as jsmith+trust@x.com. */
export function isSubAccount(userid) {
	return /\+[^@]*@/.test(cleanId(userid));
}

/** jsmith+trust@x.com -> jsmith@x.com (same rule as the Voters filter). */
export function baseAccountOf(userid) {
	return cleanId(userid).replace(/\+[^@]*?(?=@)/, '');
}

/**
 * Role flags for a holder record. Admin comes from the carlist ('^client') or the ADMIN tag; a VOA
 * also carries '^client' but is reported only as voa.
 * @param {Record<string, any> | null | undefined} holder
 * @param {string} client
 */
export function holderRoles(holder, client) {
	const tags = holder?.clients?.[client]?.tags || [];
	const carlist = holder?.carlist || [];
	const voa = tags.includes('VIEW ONLY ADMIN');
	return {
		admin: !voa && (carlist.includes('^' + client) || tags.includes('ADMIN')),
		voa,
		exec: tags.includes('EXECUTIVE'),
		supv: tags.includes('SUPERVISOR'),
		cont: tags.includes('CONTRIBUTOR'),
		inactive: tags.includes('INACTIVE'),
	};
}

/** Role labels for badges, e.g. ['Executive', 'Inactive']. */
export function roleBadges(holder, client) {
	const r = holderRoles(holder, client);
	return Object.keys(ROLE_LABELS)
		.filter((k) => r[k])
		.map((k) => ROLE_LABELS[k]);
}

/** Role chips for the Preview banner; '' for a plain user. Inactive shows by the greeting instead. */
export function roleChips(holder, client) {
	const labels = roleBadges(holder, client).filter((label) => label !== ROLE_LABELS.inactive);
	if (holder?.clients?.[client]?.consolidated_viewer) labels.push('Consolidated Viewer');
	return labels.map((label) => `<span class="pill pill--chip">${label}</span>`).join('');
}

/** Why `account` cannot be observed by anyone, or ''. */
export function cannotBeObservedReason(account, client) {
	const r = holderRoles(account, client);
	if (r.admin) return 'Admins cannot be observed.';
	if (r.voa) return 'View Only Admins (VOA) cannot be observed.';
	if (r.exec) return 'Executives cannot be observed.';
	if (r.supv) return 'Supervisors cannot be observed.';
	return '';
}

/** Why `observer` cannot observe anyone, or ''. */
export function cannotObserveReason(observer, client) {
	// Admins and VOAs may observe: they act as shareholders too (e.g. a trustee voting trusts).
	const r = holderRoles(observer, client);
	if (r.inactive) return 'Inactive accounts cannot observe other accounts.';
	if (isSubAccount(observer?.userid)) return '+subaccounts (secondary accounts) cannot observe other accounts.';
	return '';
}

/**
 * The single observer rule: '' when `observer` may observe `account`, otherwise the reason.
 * Records may be partial ({ userid } only) for accounts not yet on the cap table.
 */
export function observerPairProblem(observer, account, client) {
	if (cleanId(observer?.userid) === cleanId(account?.userid)) return 'An account cannot observe itself.';
	return cannotBeObservedReason(account, client) || cannotObserveReason(observer, client);
}
